import {
	Button,
	InfoCard,
	useUnits,
	UnitField,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { Category, Component, ComponentMetadata } from '@Types';
import { FullPage, FullPageWrapper } from '@Utils/styles';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
	addCoFComponent,
	addLoFComponent,
	getCoFComponentAssetMetadata,
	getLoFComponentAssetMetadata,
	getLoFComponentInspectionMetadata,
	setCoFComponent,
	setLoFComponent,
	setRiskScoreTable,
	setRiskScores,
	setRiskUnitScores,
	getLoFComponentProximityAttrMetadata,
	getCoFComponentProximityAttrMetadata,
	getLoFComponentCustomTableMetadata,
	getCoFComponentCustomTableMetadata,
	setRiskNullScore,
	setRiskEditScores,
} from '@Actions';
import {
	selectCofStore,
	useComponentMetadataStore,
	selectLofStore,
	selectRiskNullScore,
	selectRiskScoreTable,
} from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';

import { Grid, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useStyles } from './ComponentScore.styles';
import { MapInsertSpatial } from '@innovyze/lib_am_common/Components';
import { MapStatusType } from '@innovyze/lib_am_common/Types';
import ScoreTable from '../ScoreTable/ScoreTable.component';

interface Props {
	assetType: string;
	forLoF: boolean;
	forInspection: boolean;
	isNew: boolean;
	category?: Category;
	component: Component;
	onApply: (category: Category | undefined) => void;
	onCancel: () => void;
}

export const ComponentScore = ({
	assetType,
	forLoF,
	forInspection,
	isNew,
	category,
	component,
	onApply,
	onCancel,
}: Props) => {
	const { t } = useGlobalization();
	const classes = useStyles();
	const dispatch = useDispatch();
	const {
		getSystemValueNoIntlFormat,
		getStandardisedValue,
		getSystemUnit,
		system,
	} = useUnits();

	const lofStore = forLoF ? selectLofStore() : null;
	const cofStore = !forLoF ? selectCofStore() : null;
	const [isUnique, setIsUnique] = React.useState(true);
	const [rangeClasses, setRangeClasses] = React.useState<number>(
		component.metadata?.length ? component.metadata.length - 2 : 5,
	);
	const [componentName, setComponentName] = useState<string>(component.name);
	const [validName, setValidName] = useState<string>('');
	const [distance, setDistance] = useState<number | undefined>(
		component.maxDistance,
	);
	const [validDistance, setValidDistance] = useState<string>('');

	const scoreRows = selectRiskScoreTable();
	const riskNullScore = selectRiskNullScore();
	const metadataStore = useComponentMetadataStore();

	const createScoreRowsFromMetadata = () => {
		if (component.metadata) {
			if (component.metadataType === 'unique') {
				const newScoreRows = component.metadata.map((row, index) => ({
					ID: index.toString(),
					Score: row.value,
					Value: row.key,
				}));
				setIsUnique(true);
				dispatch(setRiskScoreTable(newScoreRows));
			} else {
				const newScoreRows = component.metadata.map((row, index) => ({
					ID: index.toString(),
					Score: row.value,
					Value: row.key,
				}));
				setIsUnique(false);
				dispatch(setRiskScoreTable(newScoreRows));
				dispatch(
					setRiskNullScore(
						component.nullScore == undefined
							? -1
							: component.nullScore,
					),
				);

				let scores: number[] = [newScoreRows.length];
				for (let i = 0; i < newScoreRows.length; i++) {
					const values = newScoreRows[i].Value.split(' ');
					if (values.length == 2) scores[i] = parseFloat(values[1]);
					else scores[i] = parseFloat(values[2]);
				}

				// If we have a unit we may need to do some covnersion stuff later
				if (
					system !== 'Metric' &&
					metadataStore &&
					metadataStore.unit &&
					metadataStore.unit.length !== 0
				) {
					const precision = metadataStore.precision ?? 0;
					// Create an array with the values in the system display value what ever that might be
					const unitScores = scores.map(score => {
						return parseFloat(
							parseFloat(
								getSystemValueNoIntlFormat(
									`${score} ${metadataStore.unit}`,
								),
							).toFixed(precision),
						);
					});
					// Now convert the rounded conversion back to metric.... I know !!!
					const internalUnit = getSystemUnit(metadataStore.unit);
					let adjustment = 1;
					// mm -> in -> cm
					if (metadataStore.unit === 'mm') adjustment = 10;

					scores = unitScores.map(score => {
						return (
							parseFloat(
								getStandardisedValue(
									`${score} ${internalUnit}`,
								),
							) * adjustment
						);
					});
					dispatch(setRiskUnitScores(unitScores));
				}

				const stringScores = scores.map(score => {
					return score.toString();
				});
				dispatch(setRiskEditScores(stringScores));
				dispatch(setRiskScores(scores));
			}
		}
	};

	const updateComponent = () => {
		let index = 0;
		const newMetadata = scoreRows.map(row => ({
			key: row.Value,
			value: row.Score,
			ix: index++,
		}));
		component.metadata = newMetadata;
		component.nullScore = riskNullScore;
		component.name = componentName;
		component.maxDistance = distance;
	};

	useEffect(() => {
		if (component.field || component.layer) {
			if (!isNew) {
				createScoreRowsFromMetadata();
			}
		}
	}, [metadataStore]);

	const fetchMetadata = () => {
		if (component.field || component.layer) {
			switch (component.type) {
				case 'ProximityAttribute': {
					if (forLoF) {
						dispatch(
							getLoFComponentProximityAttrMetadata({
								assetType: assetType,
								field: component.field ?? '',
								fieldAlias: component.layer ?? '',
								layerName: component.layer,
							}),
						);
					} else {
						dispatch(
							getCoFComponentProximityAttrMetadata({
								assetType: assetType,
								field: component.field ?? '',
								fieldAlias: component.layer ?? '',
								layerName: component.layer,
							}),
						);
					}
					break;
				}
				case 'Spatial Proximity': {
					if (forLoF) {
						dispatch(
							getLoFComponentAssetMetadata({
								assetType: assetType,
								field: 'proximity',
								fieldAlias: component.layer ?? '',
							}),
						);
					} else {
						dispatch(
							getCoFComponentAssetMetadata({
								assetType: assetType,
								field: 'proximity',
								fieldAlias: component.layer ?? '',
							}),
						);
					}
					break;
				}
				case 'customTable': {
					if (forLoF) {
						dispatch(
							getLoFComponentCustomTableMetadata({
								assetType: assetType,
								field: component.field ?? '',
								fieldAlias: component.fieldAlias ?? '',
								layerName: component.layer,
							}),
						);
					} else {
						dispatch(
							getCoFComponentCustomTableMetadata({
								assetType: assetType,
								field: component.field ?? '',
								fieldAlias: component.fieldAlias ?? '',
								layerName: component.layer,
							}),
						);
					}
					break;
				}
				default: {
					if (forLoF) {
						if (forInspection) {
							dispatch(
								getLoFComponentInspectionMetadata({
									assetType: assetType,
									field: component.field ?? '',
									fieldAlias: component.fieldAlias ?? '',
								}),
							);
						} else {
							dispatch(
								getLoFComponentAssetMetadata({
									assetType: assetType,
									field: component.field ?? '',
									fieldAlias: component.fieldAlias ?? '',
								}),
							);
						}
					} else {
						dispatch(
							getCoFComponentAssetMetadata({
								assetType: assetType,
								field: component.field ?? '',
								fieldAlias: component.fieldAlias ?? '',
							}),
						);
					}
					break;
				}
			}
		}
	};

	const createDefaultRangeValues = () => {
		if (metadataStore && metadataStore.range) {
			const metadata: ComponentMetadata[] = new Array(rangeClasses + 2);
			const defaultScore: number[] = [metadata.length];
			const range = metadataStore.range.max - metadataStore.range.min;
			let interval = range / rangeClasses;
			const precision: number = metadataStore.precision ?? 0;

			if (metadataStore.precision === 0) interval = Math.round(interval);
			else if (metadataStore.precision != undefined)
				interval =
					Math.round(interval * Math.pow(10, precision)) /
					Math.pow(10, precision);

			let first = metadataStore.range.min;
			let pos = 0;

			defaultScore[pos] = first;
			metadata[pos] = {
				key: `< ${first}`,
				value: -1,
				ix: pos++,
			};

			for (let i = 0; i < rangeClasses - 1; i++) {
				let next = first + interval;
				// Do another round as for some reason javascript still having rounding issues
				if (metadataStore.precision === 0) next = Math.round(next);
				else if (metadataStore.precision != undefined)
					next =
						Math.round(next * Math.pow(10, precision)) /
						Math.pow(10, precision);

				metadata[pos] = {
					key: `${first} <= ${next}`,
					value: -1,
					ix: pos++,
				};
				defaultScore[pos] = next;
				first = next;
			}

			defaultScore[pos] = metadataStore.range.max;
			metadata[pos] = {
				key: `${first} <= ${metadataStore.range.max}`,
				value: -1,
				ix: pos++,
			};

			defaultScore[pos] = metadataStore.range.max;
			metadata[pos] = {
				key: `> ${metadataStore.range.max}`,
				value: -1,
				ix: pos++,
			};

			component.metadata = metadata;
			component.metadataType = 'range';

			const stringScores = defaultScore.map(score => {
				return score.toString();
			});
			dispatch(setRiskEditScores(stringScores));
			dispatch(setRiskScores(defaultScore));
		}
	};

	useEffect(() => {
		fetchMetadata();
		if (
			!isNew &&
			component.metadata &&
			component.metadata.length - 2 == rangeClasses
		)
			return;
		createDefaultRangeValues();
		createScoreRowsFromMetadata();
	}, [rangeClasses]);

	useEffect(() => {
		if (metadataStore && isNew) {
			if (metadataStore.unique && metadataStore.unique.length > 0) {
				// Convert the default settings into the component version
				let metadata = metadataStore.unique.map(item => ({
					key: item,
					value: -1,
				}));

				// Add the out of range if required
				if (
					!forInspection &&
					metadata.find(value => value.key === '--') === undefined
				) {
					metadata = [{ key: '--', value: -1 }, ...metadata];
				}

				component.metadata = metadata;
				component.metadataType = 'unique';
				createScoreRowsFromMetadata();
			} else if (metadataStore.range) {
				if (
					!isNew &&
					component.metadata &&
					component.metadata.length - 2 == rangeClasses
				)
					return;
				createDefaultRangeValues();
				createScoreRowsFromMetadata();
			}
		}
	}, [metadataStore]);

	const checkTableValid = () => {
		if (scoreRows && scoreRows.length > 0) {
			return scoreRows.findIndex(scoreRow => scoreRow.Score < 0) < 0;
		}

		return true;
	};

	const validateName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let error = '';
		if (name.trim().length == 0) {
			error = t('Enter a valid name');
		} else if (
			// Ignore the unique check if the name has not changed
			component.name.toUpperCase() !== name
		) {
			let valid = true;
			if (forLoF) {
				valid =
					lofStore?.categories.find(cat =>
						cat.name === category?.name
							? cat.components.find(
									component =>
										component.name.toUpperCase() == name,
							  )
							: undefined,
					) === undefined;
			} else {
				valid =
					cofStore?.categories.find(cat =>
						cat.name === category?.name
							? cat.components.find(
									catComponent =>
										catComponent.name.toUpperCase() == name,
							  )
							: undefined,
					) === undefined;
			}

			if (!valid) error = t('Enter a unique name');
		}

		setValidName(error);
		setComponentName(event.target.value);
	};

	const validateDistance = (
		event: ChangeEvent<
			HTMLInputElementExtended | HTMLTextAreaElementExtended
		>,
	) => {
		const distance = parseFloat(event.target.value);
		let error = '';
		if (isNaN(distance) || distance < 0) {
			error = t('Enter a valid distance');
		} else {
			setDistance(distance);
		}
		setValidDistance(error);
	};

	const componentSetup = () => {
		let setup = <Grid item xs={8}></Grid>;
		switch (component.type) {
			case 'attribute':
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Type"
								defaultValue={t('Attribute')}
								label={t('Type')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Field"
								defaultValue={component.fieldAlias ?? ''}
								label={t('Field')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={4}></Grid>
					</>
				);
				break;
			case 'Spatial Proximity':
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Type"
								defaultValue={t('Spatial Proximity')}
								label={t('Type')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="DataSource"
								defaultValue={component.layer ?? ''}
								label={t('Data Source')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={4}></Grid>
					</>
				);
				break;
			case 'ProximityAttribute':
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Type"
								defaultValue={t(
									'Spatial Proximity -- Attribute',
								)}
								label={t('Type')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="DataSource"
								defaultValue={component.layer ?? ''}
								label={t('Data Source')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Field"
								defaultValue={component.field ?? ''}
								label={t('Field')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<UnitField
								fullWidth
								name="Distance"
								unit="m"
								step="1"
								min={0}
								defaultValue={
									distance ? distance.toString() : '0'
								}
								label={t('Distance')}
								variant="outlined"
								onChange={validateDistance}
								helperText={validDistance}
								error={validDistance !== ''}
							/>
						</Grid>
					</>
				);
				break;
			case 'customTable':
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Type"
								defaultValue={t('Custom Table')}
								label={t('Type')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="DataSource"
								defaultValue={component.layer ?? ''}
								label={t('Data Source')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							<TextField
								fullWidth
								disabled
								name="Field"
								defaultValue={component.fieldAlias ?? ''}
								label={t('Field')}
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={2}></Grid>
					</>
				);
				break;
		}

		return setup;
	};

	const [hasError, setHasError] = useState<boolean>(!checkTableValid());

	let hasMap =
		(component.type === 'Spatial Proximity' ||
			component.type === 'ProximityAttribute') ??
		false;

	if (!useIsFeatureEnabled('info-360-am-risk-map')) {
		hasMap = false;
	}

	const layerName = component.layer ?? '';

	const mapInsert = (
		<Grid xs={4} item paddingLeft={'20px'}>
			<InfoCard title={t('Map')} applyBodyPadding={false}>
				<div className={classes.map}>
					<MapInsertSpatial
						layerName={layerName}
						showSystemTypeLayers={true}
						logLevel={MapStatusType.WARN}
					/>
				</div>
			</InfoCard>
		</Grid>
	);

	useEffect(() => {
		setHasError(!checkTableValid());
	}, [scoreRows]);

	return (
		<FullPageWrapper
			title={isNew ? t('Add New Component') : t('Edit Component')}
			applyPadding={true}
			headerUnderline
			className={classes.header}
			secondary={
				<div>
					<Button
						variant="contained"
						color="secondary"
						style={{ marginRight: 16 }}
						onClick={() => {
							if (onCancel) onCancel();
						}}>
						{t('Cancel')}
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={
							hasError || validName != '' || validDistance != ''
						}
						onClick={() => {
							if (!hasError) {
								if (forLoF && category) {
									const originalLofName = component.name;
									updateComponent();
									if (isNew) {
										dispatch(
											addLoFComponent({
												categoryName: category.name,
												component: component,
											}),
										);
									} else {
										dispatch(
											setLoFComponent({
												categoryName: category.name,
												componentName: originalLofName,
												component: component,
											}),
										);
									}
									if (onApply) onApply(category);
								} else if (!forLoF && category) {
									const originalCofName = component.name;
									updateComponent();
									if (isNew) {
										dispatch(
											addCoFComponent({
												categoryName: category.name,
												component: component,
											}),
										);
									} else {
										dispatch(
											setCoFComponent({
												categoryName: category.name,
												componentName: originalCofName,
												component: component,
											}),
										);
									}
									if (onApply) onApply(undefined);
								}
							}
						}}>
						{isNew ? t('Create') : t('Update')}
					</Button>
				</div>
			}>
			<FullPage>
				<Grid container>
					<Grid item xs={4}>
						<TextField
							fullWidth
							name="ComponentName"
							defaultValue={componentName}
							label={t('Component Name')}
							variant="outlined"
							onChange={validateName}
							helperText={validName}
							error={validName !== ''}
						/>
					</Grid>
					{componentSetup()}
				</Grid>
				<Grid container paddingTop={'20px'}>
					<Grid item xs={hasMap ? 8 : 12}>
						<ScoreTable
							isLoading={metadataStore?.isLoading}
							isUnique={isUnique}
							isNew={isNew}
							scoreRows={scoreRows}
							rangeClasses={rangeClasses}
							setRangeClasses={setRangeClasses}
							hasError={hasError}
							precision={
								metadataStore
									? metadataStore.precision
									: undefined
							}
							unit={
								metadataStore ? metadataStore.unit : undefined
							}
							currency={
								metadataStore
									? metadataStore.currency
									: undefined
							}
						/>
					</Grid>
					{hasMap ? mapInsert : []}
				</Grid>
			</FullPage>
		</FullPageWrapper>
	);
};
